import { cn } from '../lib/cn';

export type UpNextVariant = 'countdown' | 'play' | 'quiz' | 'locked' | 'end';

/**
 * The end-of-lesson card, rendered as an overlay INSIDE the player wrapper so
 * it survives fullscreen. Purely presentational: the countdown ticks in the
 * host page, which owns navigation.
 */
export function UpNextCard({
  variant,
  title,
  detail,
  secondsLeft,
  autoplay,
  onToggleAutoplay,
  primaryLabel,
  onPrimary,
  onCancel,
}: {
  variant: UpNextVariant;
  title: string;
  detail?: string;
  secondsLeft?: number;
  autoplay?: boolean;
  onToggleAutoplay?: (next: boolean) => void;
  primaryLabel?: string;
  onPrimary?: () => void;
  onCancel?: () => void;
}) {
  const eyebrow =
    variant === 'quiz' ? 'Chapter quiz' : variant === 'end' ? 'Course content' : 'Up next';
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black/75 p-4">
      <div className="w-full max-w-sm rounded-lg bg-black/60 p-4 text-center text-white">
        <p className="text-[11px] uppercase tracking-wide text-white/60">{eyebrow}</p>
        <p className="mt-1 line-clamp-2 text-sm font-semibold">{title}</p>
        {detail && <p className="mt-1 text-xs text-white/70">{detail}</p>}
        {variant === 'countdown' && secondsLeft != null && (
          <p className="mt-2 text-xs tabular-nums text-white/80">Playing in {secondsLeft}…</p>
        )}
        {(onPrimary || onCancel) && (
          <div className="mt-3 flex items-center justify-center gap-2">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="rounded-md border border-white/30 px-3 py-1.5 text-xs font-medium text-white/90 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Cancel
              </button>
            )}
            {onPrimary && primaryLabel && (
              <button
                type="button"
                onClick={onPrimary}
                className={cn(
                  'rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
              >
                {primaryLabel}
              </button>
            )}
          </div>
        )}
        {onToggleAutoplay && (
          <label className="mt-3 flex items-center justify-center gap-2 text-[11px] text-white/70">
            <input
              type="checkbox"
              checked={!!autoplay}
              onChange={(e) => onToggleAutoplay(e.target.checked)}
              className="h-3.5 w-3.5 accent-current"
            />
            Autoplay next lessons
          </label>
        )}
      </div>
    </div>
  );
}
